import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatCurrency } from '@/lib/currency'
import { useT } from '@/i18n/I18nProvider'
import type { Category } from '@/types/db'

export interface AdjustItem {
  cat: Category
  from: number
  to: number
}

// Ajuste de orçamento: alinha o orçado ao consumido do mês.
//  - semOrc/estourou (SOBE o orçado): sempre aplicados — nunca é surpresa, só
//    reconhece um gasto que já aconteceu.
//  - sobrou (REDUZ o orçado): cada item exige o usuário marcar explicitamente.
//    Separado em dois grupos porque o risco é diferente: "zerado" (nada consumido
//    ainda) é o caso clássico de reserva de propósito (ex.: orçado 200, ninguém
//    gastou nada AINDA); "com diferença" já teve algum consumo, sobrando só o resto.
export function AdjustBudgetDialog({
  open,
  onOpenChange,
  semOrc,
  estourou,
  sobrou,
  monthLabel,
  baseCurrency,
  categoryName,
  applying,
  onApply,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  semOrc: { cat: Category; to: number }[]
  estourou: AdjustItem[]
  sobrou: AdjustItem[]
  monthLabel: string
  baseCurrency: string
  categoryName: (cat: Category) => string
  applying: boolean
  onApply: (rows: { categoryId: string; amount: number }[]) => void
}) {
  const { t } = useT()
  const [checked, setChecked] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (open) setChecked(new Set())
  }, [open])

  const { zerados, comDiferenca } = useMemo(() => {
    const zerados: AdjustItem[] = []
    const comDiferenca: AdjustItem[] = []
    for (const x of sobrou) (x.to <= 0.005 ? zerados : comDiferenca).push(x)
    return { zerados, comDiferenca }
  }, [sobrou])

  const raiseCount = semOrc.length + estourou.length
  const increaseTotal =
    semOrc.reduce((s, x) => s + x.to, 0) + estourou.reduce((s, x) => s + (x.to - x.from), 0)
  const selectedSobrou = sobrou.filter((x) => checked.has(x.cat.id))
  const reduceTotal = selectedSobrou.reduce((s, x) => s + (x.from - x.to), 0)
  const totalToApply = raiseCount + selectedSobrou.length

  function toggle(id: string) {
    setChecked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  function toggleGroup(items: AdjustItem[]) {
    const ids = items.map((x) => x.cat.id)
    const allChecked = ids.every((id) => checked.has(id))
    setChecked((prev) => {
      const next = new Set(prev)
      for (const id of ids) (allChecked ? next.delete(id) : next.add(id))
      return next
    })
  }

  function apply() {
    const rows = [
      ...semOrc.map((x) => ({ categoryId: x.cat.id, amount: x.to })),
      ...estourou.map((x) => ({ categoryId: x.cat.id, amount: x.to })),
      ...selectedSobrou.map((x) => ({ categoryId: x.cat.id, amount: x.to })),
    ]
    onApply(rows)
  }

  function group(title: string, hint: string, items: AdjustItem[]) {
    if (items.length === 0) return null
    const allChecked = items.every((x) => checked.has(x.cat.id))
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">{title}</p>
          <button type="button" className="text-primary text-xs hover:underline" onClick={() => toggleGroup(items)}>
            {allChecked ? t('bud.adjustUnselectAll') : t('bud.adjustSelectAll')}
          </button>
        </div>
        <p className="text-muted-foreground text-xs">{hint}</p>
        <div className="divide-border divide-y rounded-lg border">
          {items.map((x) => (
            <label
              key={x.cat.id}
              className="hover:bg-accent/30 flex cursor-pointer items-center gap-2 px-3 py-2 text-sm"
            >
              <Checkbox checked={checked.has(x.cat.id)} onCheckedChange={() => toggle(x.cat.id)} />
              <span className="flex-1 truncate">{categoryName(x.cat)}</span>
              <span className="text-muted-foreground text-xs tabular-nums">
                {formatCurrency(x.from, baseCurrency)} → {formatCurrency(x.to, baseCurrency)}
              </span>
            </label>
          ))}
        </div>
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90svh] flex-col sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('bud.adjustDialogTitle', { month: monthLabel })}</DialogTitle>
          <DialogDescription>{t('bud.adjustDialogDesc')}</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
          {raiseCount > 0 && (
            <div className="bg-muted/40 rounded-lg border p-3 text-sm">
              {semOrc.length > 0 && (
                <p>
                  <b>{semOrc.length}</b> {t('bud.adjustSemOrcPre')} <b>{t('bud.adjustSemOrcBold')}</b>{' '}
                  {t('bud.adjustSemOrcPost')}
                </p>
              )}
              {estourou.length > 0 && (
                <p>
                  <b>{estourou.length}</b> {t('bud.adjustEstourouPre')} <b>{t('bud.adjustEstourouBold')}</b>{' '}
                  {t('bud.adjustEstourouPost')}
                </p>
              )}
              <p className="text-muted-foreground mt-1">
                {t('bud.adjustTotalIncrease')} <b className="text-foreground">{formatCurrency(increaseTotal, baseCurrency)}</b>
              </p>
            </div>
          )}

          {group(t('bud.adjustZeradosTitle'), t('bud.adjustZeradosHint'), zerados)}
          {group(t('bud.adjustSobrouTitle'), t('bud.adjustSobrouHint'), comDiferenca)}

          {selectedSobrou.length > 0 && (
            <p className="text-muted-foreground text-sm">
              {t('bud.adjustReduceTotal')}{' '}
              <b className="text-foreground">{formatCurrency(reduceTotal, baseCurrency)}</b>
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={apply} disabled={totalToApply === 0 || applying}>
            {applying ? t('common.saving') : t('bud.adjustConfirmN', { n: totalToApply })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
